import {
  ChevronDownIcon,
  Loading01Icon,
  RulerIcon,
} from '@langchain/untitled-ui-icons';
import { Autocomplete, Option, Select } from '@mui/joy';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import useToast from '@/components/Toast/useToast';
import { TruncatedTextWithTooltip } from '@/components/TruncatedTextWithTooltip.tsx';
import { GroupedTabs } from '@/design-system/components/GroupedTabs/GroupedTabs';
import {
  useFeedbackConfigs,
  useUpdateFeedbackConfigMutation,
} from '@/hooks/useSwr';
import { SessionSchema } from '@/types/schema';
import { useClickLogger } from '@/utils/datadog/useClickLogger';
import { getColorByString } from '@/utils/get-color-by-string';

export const BaselineSessionSelectPopover = ({
  sessions,
  baselineSessionId,
  setBaselineSessionId,
  aggregateFeedbackKeys,
  selectedFeedbackKey,
  setSelectedFeedbackKey,
  mutateDelta,
  onFeedbackConfigUpdate,
}: {
  sessions: Pick<SessionSchema, 'id' | 'name'>[];
  baselineSessionId?: string;
  setBaselineSessionId: (sessionId: string) => void;
  aggregateFeedbackKeys: string[];
  selectedFeedbackKey?: string;
  setSelectedFeedbackKey: (feedbackKey: string) => void;
  mutateDelta?: () => void;
  onFeedbackConfigUpdate?: () => void;
}) => {
  return (
    <Popover>
      <PopoverTrigger>
        <div className="h-[34px] rounded-md border border-secondary bg-background px-2 py-[5px] font-medium">
          <div className="flex items-center gap-2">
            <RulerIcon className="h-4 w-4" />
            <span className="text-sm">Baseline</span>
            <div className="flex items-center gap-2 rounded-[3px] bg-tertiary px-1 py-0.5 text-xs">
              {selectedFeedbackKey && (
                <span
                  className="h-2 w-2 min-w-2 shrink-0 rounded-sm"
                  style={{
                    backgroundColor: getColorByString(selectedFeedbackKey),
                  }}
                />
              )}
              <TruncatedTextWithTooltip
                text={selectedFeedbackKey}
                className="max-w-[100px] truncate"
              />
            </div>
            <ChevronDownIcon className="h-4 w-4" />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className="m-0 w-[400px] px-0 py-2.5"
      >
        <BaselineSessionSelect
          sessions={sessions}
          baselineSessionId={baselineSessionId}
          setBaselineSessionId={setBaselineSessionId}
          aggregateFeedbackKeys={aggregateFeedbackKeys}
          selectedFeedbackKey={selectedFeedbackKey}
          setSelectedFeedbackKey={setSelectedFeedbackKey}
          mutateDelta={mutateDelta}
          onFeedbackConfigUpdate={onFeedbackConfigUpdate}
        />
      </PopoverContent>
    </Popover>
  );
};

const BaselineSessionSelect = ({
  sessions,
  baselineSessionId,
  setBaselineSessionId,
  aggregateFeedbackKeys,
  selectedFeedbackKey,
  setSelectedFeedbackKey,
  mutateDelta,
  onFeedbackConfigUpdate,
}: {
  sessions: Pick<SessionSchema, 'id' | 'name'>[];
  baselineSessionId?: string;
  setBaselineSessionId: (sessionId: string) => void;
  aggregateFeedbackKeys: string[];
  selectedFeedbackKey?: string;
  setSelectedFeedbackKey: (feedbackKey: string) => void;
  mutateDelta?: () => void;
  onFeedbackConfigUpdate?: () => void;
}) => {
  const logClick = useClickLogger();
  const { createToast } = useToast();

  const feedbackConfig = useFeedbackConfigs(
    {
      skip: !selectedFeedbackKey,
    },
    {
      key: selectedFeedbackKey ? [selectedFeedbackKey] : [],
      read_after_write: true,
    }
  );
  const currFeedbackConfig = feedbackConfig.data?.find(
    (config) => config.feedback_key === selectedFeedbackKey
  );
  const isLowerScoreBetter = currFeedbackConfig?.is_lower_score_better;

  const updateFeedbackConfig = useUpdateFeedbackConfigMutation({
    onError: (error) => {
      createToast({
        title: `Failed to update feedback direction`,
        description: error.message,
        error: true,
      });
    },
    onSuccess: () => {
      mutateDelta?.();
    },
  });

  return (
    <div className="mx-3 flex flex-col gap-3">
      <SettingsSection title="Baseline experiment">
        <SelectBaselineExperiment
          sessions={sessions}
          baselineSessionId={baselineSessionId}
          setBaselineSessionId={setBaselineSessionId}
        />
      </SettingsSection>
      <div className="grid grid-cols-2 gap-2">
        <SettingsSection title="Feedback key">
          <FeedbackSelector
            aggregateFeedbackKeys={aggregateFeedbackKeys}
            selectedFeedbackKey={selectedFeedbackKey}
            setSelectedFeedbackKey={setSelectedFeedbackKey}
          />
        </SettingsSection>

        <SettingsSection title="Is a higher score better?">
          <FeedbackDirectionToggle
            isLowerScoreBetter={!!isLowerScoreBetter}
            isMutating={updateFeedbackConfig.isMutating}
            setIsLowerScoreBetter={async (newValue) => {
              logClick('track_regressions_direction_selected', {
                direction: newValue,
              });
              if (selectedFeedbackKey) {
                await updateFeedbackConfig.trigger({
                  json: {
                    feedback_key: selectedFeedbackKey,
                    is_lower_score_better: newValue === 'No',
                  },
                });
              }
              onFeedbackConfigUpdate?.();
            }}
          />
        </SettingsSection>
      </div>
    </div>
  );
};

export const FeedbackDirectionToggle = ({
  isLowerScoreBetter,
  isMutating,
  setIsLowerScoreBetter,
}: {
  isLowerScoreBetter: boolean;
  isMutating: boolean;
  setIsLowerScoreBetter: (newValue: string) => void;
}) => {
  return (
    <GroupedTabs
      value={isLowerScoreBetter ? 'No' : 'Yes'}
      onChange={setIsLowerScoreBetter}
      options={[
        {
          value: 'Yes',
          display: (
            <div className="w-6">
              {isMutating && isLowerScoreBetter ? (
                <Loading01Icon className="mx-auto h-3 w-3 animate-spin" />
              ) : (
                'Yes'
              )}
            </div>
          ),
        },
        {
          value: 'No',
          display: (
            <div className="mx-auto w-6">
              {isMutating && !isLowerScoreBetter ? (
                <Loading01Icon className="mx-auto h-3 w-3 animate-spin" />
              ) : (
                'No'
              )}
            </div>
          ),
        },
      ]}
    />
  );
};

const SettingsSection = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold">{title}</span>
        {description && <span className="text-secondary">{description}</span>}
      </div>
      {children}
    </div>
  );
};

const SelectBaselineExperiment = ({
  sessions,
  baselineSessionId,
  setBaselineSessionId,
}: {
  sessions: Pick<SessionSchema, 'id' | 'name'>[];
  baselineSessionId?: string;
  setBaselineSessionId: (sessionId: string) => void;
}) => {
  return (
    <Select
      value={baselineSessionId}
      onChange={(_, value) => setBaselineSessionId(value ?? '')}
      slotProps={{
        listbox: {
          sx: { maxHeight: '300px', overflow: 'auto' },
        },
      }}
      placeholder="Select a baseline"
      size="sm"
    >
      {sessions.map((session) => (
        <Option key={session.id} value={session.id}>
          <span>{session.name}</span>
        </Option>
      ))}
    </Select>
  );
};

const FeedbackSelector = ({
  aggregateFeedbackKeys,
  selectedFeedbackKey,
  setSelectedFeedbackKey,
}: {
  aggregateFeedbackKeys: string[];
  selectedFeedbackKey?: string;
  setSelectedFeedbackKey: (value: string) => void;
}) => {
  return (
    <Autocomplete
      value={selectedFeedbackKey}
      onChange={(_, value) => {
        if (value) {
          setSelectedFeedbackKey(value);
        }
      }}
      placeholder="Select a feedback key"
      options={aggregateFeedbackKeys}
      size="sm"
      disableClearable
      sx={{ fontWeight: 500, paddingX: 1, paddingY: 0.5 }}
    />
  );
};
